import type { TicketDetailReview } from '@nightshift/ui-types';

/**
 * Reviewer attempts grouped by the MR head they judged. A wave is every reviewer that ran
 * against one SHA; the ticket re-reviews after each push, so the newest wave is the one
 * that matters and older waves are history. Ordered newest first.
 */
export interface ReviewWave {
  /** Null when the attempts never pinned a head (a review that started before the MR existed). */
  sha: string | null;
  stale: boolean;
  reviews: TicketDetailReview[];
}

export function groupReviewWaves(reviews: readonly TicketDetailReview[]): ReviewWave[] {
  const waves: ReviewWave[] = [];
  const byKey = new Map<string, ReviewWave>();
  for (const review of reviews) {
    const key = review.mrHeadSha ?? '';
    let wave = byKey.get(key);
    if (!wave) {
      wave = { sha: review.mrHeadSha, stale: review.stale, reviews: [] };
      byKey.set(key, wave);
      waves.push(wave);
    }
    wave.reviews.push(review);
    wave.stale = wave.stale && review.stale;
  }
  return waves.reverse();
}

export type ReviewVerdictLabel =
  | 'Pass'
  | 'Pass — verification gaps'
  | 'Fail'
  | 'Running'
  | 'Malformed'
  | 'Cancelled';

function reviewHasVerificationGaps(review: TicketDetailReview): boolean {
  return (
    review.verdict === 'pass' &&
    review.coverage.total > 0 &&
    review.criteria.some((criterion) => criterion.index > 0 && criterion.status !== 'met')
  );
}

/** What to print on the verdict chip. Running covers every non-terminal status. */
export function reviewVerdictLabel(review: TicketDetailReview): ReviewVerdictLabel {
  if (review.verdict === 'pass') {
    return reviewHasVerificationGaps(review) ? 'Pass — verification gaps' : 'Pass';
  }
  if (review.verdict === 'fail') return 'Fail';
  if (review.malformed) return 'Malformed';
  if (review.status === 'Cancelled' || review.status === 'Interrupted' || review.status === 'Superseded') {
    return 'Cancelled';
  }
  return 'Running';
}

/**
 * One line of what the newest wave decided, for the merge panel header:
 * "2 of 2 reviewers passed", "1 of 2 failed", "review still running".
 */
export function summariseWave(wave: ReviewWave | undefined): string {
  if (!wave || wave.reviews.length === 0) return 'No AI review has run for this head.';
  const labels = wave.reviews.map(reviewVerdictLabel);
  const total = labels.length;
  const passed = labels.filter((label) => label === 'Pass' || label === 'Pass — verification gaps').length;
  const gaps = labels.filter((label) => label === 'Pass — verification gaps').length;
  const failed = labels.filter((label) => label === 'Fail').length;
  const running = labels.filter((label) => label === 'Running').length;
  const noun = total === 1 ? 'reviewer' : 'reviewers';
  if (running > 0) return `${running} of ${total} ${noun} still running.`;
  if (failed > 0) return `${failed} of ${total} ${noun} failed.`;
  if (passed === total) {
    return gaps > 0
      ? `${passed} of ${total} ${noun} passed; ${gaps} reported verification gaps.`
      : `${passed} of ${total} ${noun} passed.`;
  }
  return `${passed} of ${total} ${noun} passed; the rest left no verdict.`;
}
